import { PagingMeta } from "@/lib/paging"
import { FlightCabinTypes } from "./chat"
import type { Timestamp } from "./date"
import { PlaceRef } from "./entity"
import type { PlacePhoto } from "./place"

export type AgentRef = `pa-${string}`
export type AgentRunRef = `ar-${string}`

const AgentRefRE = /^pa-[a-zA-Z0-9]{8}$/
const AgentRunRefRE = /^ar-[a-zA-Z0-9]{8}$/

export function isAgentRef(value: string): value is AgentRef {
  return AgentRefRE.test(value)
}

export function isAgentRunRef(value: string): value is AgentRunRef {
  return AgentRunRefRE.test(value)
}

export type Cadence = "daily"

export type ProactiveAgentStatus =
  | "active"
  | "paused"
  | "cancelled"
  | "expired"
  | "superseded"

export interface FixedPropertyConfig {
  mode: "fixed_property"
  entity_ref?: PlaceRef
  hotel_name: string
  check_in: string
  check_out: string
  adults: number
  children: number
  child_ages?: number[]
  rooms?: number
  target_price: number
  currency: string
  /** Observed nightly price when created or last edited. Nuitee rates at or below 50% of this value are excluded; omitting it disables the filter. */
  baseline_price?: number
  hotel_room_entity_ref?: string
  provider?: string
  provider_room_id?: string
  room_name?: string
}

export type HotelPriceAlertConfig = FixedPropertyConfig

export interface FlightPriceAlertJourney {
  origin: string
  destination: string
  date: string
  origin_city?: string
  destination_city?: string
}

export interface FlightPriceAlertAirline {
  name: string
  iata: string
  logo_url?: string | null
}

export interface FlightPriceAlertSlice {
  origin: string
  destination: string
  duration_minutes: number
  stops?: number
  departs_at: string
  arrives_at: string
  departure_time_zone: string
  arrival_time_zone: string
  airlines: FlightPriceAlertAirline[]
  flight_numbers?: string[]
}

export interface FlightPriceAlertConfig {
  journeys: FlightPriceAlertJourney[]
  adults: number
  children: number
  infants_in_seat?: number
  infants_on_lap?: number
  cabin_class: FlightCabinTypes
  max_stops?: number
  excluded_airline_codes?: string[]
  included_airline_codes?: string[]
  earliest_departure_time?: string
  latest_departure_time?: string
  max_fare?: number
  flight_numbers?: string[]
  slices?: FlightPriceAlertSlice[]
  reference_price: number
  target_price: number
  currency: string
  /** Opaque server-generated version of this persisted configuration. */
  config_revision?: string
  /** Legacy compatibility token; new code should use config_revision for run matching. */
  watch_key?: string
  source_chat_id: number
  source_agent_session_id?: number
  source_search_group?: string
  source_quote_ref?: string
  source_intent_key?: string
  source_label?: string
  /** Whether the opaque source group represents one route or a whole search. */
  source_group_kind?: FlightPriceAlertSourceGroupKind
  /** This watch came from a priced option that was not shown in the search results. */
  source_unshown?: boolean
  provider_key?: string
}

export type FlightPriceAlertSourceGroupKind = "route" | "search"

export interface WatchedHotelSearchConfig {
  source_intent_key: string
  source_label: string
  source_query: string
  source_agent_session_id?: number
  source_message_id?: number
  source_search_group?: string
  location?: string
  check_in?: string
  check_out?: string
  adults?: number
  children?: number
  rooms?: number
  budget_per_night?: number
  budget_currency?: string
  features?: string[]
  explicit_request?: boolean
}

interface AbstractAgentSummary {
  id: number
  ref: AgentRef
  name: string
  agent_type_label?: string
  created_by: "user_created" | "auto_created"
  cadence: Cadence
  status: ProactiveAgentStatus

  /**
   * TODO: would be best to use Timestamp instead of ISO 8601 strings,
   * per usual pattern in this app, but these are now in use by iOS so
   * we need to handle old clients.
   */
  last_run_at?: string
  next_run_at?: string
  expires_at?: string

  last_run?: AgentRun
  photo_url?: string

  /** Site-relative path reopening this flight watch, with the latest matching itinerary selected when available. */
  view_flights_url?: string
  /** Rollup identity shared by every alert from one fuzzy search. */
  source_intent_key?: string
  /** Label for that search, e.g. "Hoi An beachfront hotel search". */
  source_label?: string
  /** Meaning of an opaque flight source group id. */
  source_group_kind?: FlightPriceAlertSourceGroupKind
  /** The customer's ask in their own words. */
  source_query?: string
  /** True for the search itself, when nothing was watchable yet. */
  watched_search?: boolean
  /** True when the user explicitly asked the bot to watch this search. */
  explicit_request?: boolean
}

export interface HotelPriceAlertAgentSummary extends AbstractAgentSummary {
  agent_type: "hotel_price_alert"
  agent_config: HotelPriceAlertConfig
}

export interface FlightPriceAlertAgentSummary extends AbstractAgentSummary {
  agent_type: "flight_price_alert"
  agent_config: FlightPriceAlertConfig
}

export interface WatchedHotelSearchAgentSummary extends AbstractAgentSummary {
  agent_type: "watched_hotel_search"
  agent_config: WatchedHotelSearchConfig
}

export interface FlightStatusAlertAgentSummary extends AbstractAgentSummary {
  agent_type: "flight_status_alert"
  agent_config: FlightStatusAlertConfig
}

export type AgentSummary =
  | HotelPriceAlertAgentSummary
  | FlightPriceAlertAgentSummary
  | WatchedHotelSearchAgentSummary
  | FlightStatusAlertAgentSummary

export interface FlightPriceAlertMatch {
  id: number
  status: ProactiveAgentStatus
  config_revision?: string
  source_intent_key?: string
}

export interface FlightPriceAlertMatchResponse {
  active_price_alert?: FlightPriceAlertMatch | null
  active_search_price_alert?: FlightPriceAlertMatch | null
}

/** Alerts from one search, presented as the single search the user made. */
export interface WatchedSearchGroup {
  sourceIntentKey: string
  sourceGroupKind?: FlightPriceAlertSourceGroupKind
  label: string
  query?: string
  /** The price alerts created from the search. Empty while nothing is watchable. */
  alerts: AgentSummary[]
  /** The search row itself, present only when it produced no alerts. */
  watchedSearch?: AgentSummary
  /** False when every member is paused, so the header can offer Resume. */
  active: boolean
  /**
   * Members whose latest check matched, in list order. A collapsed group hides
   * the only row worth acting on, so the header rolls these up instead.
   */
  deals: AgentSummary[]
}

export interface ProactiveAgentListResponse {
  proactive_agents: AgentSummary[]
  meta: PagingMeta
}

export interface CreateHotelPriceAlertPayload {
  agent_type: "hotel_price_alert"
  name: string
  agent_config: HotelPriceAlertConfig
}

export interface FlightStatusAlertConfig {
  airline_iata: string
  flight_number: string
  /** Airport-local departure date, YYYY-MM-DD */
  departure_date: string
  dep_iata: string
  arr_iata: string
  departs_at: string
  arrives_at: string
  itinerary_event_id?: number
  flight_booking_id?: number
  calendar_travel_event_id?: number
  /** Set by the backend once the shared tracked flight is provisioned */
  tracked_flight_id?: number
}

export interface CreateFlightStatusAlertPayload {
  agent_type: "flight_status_alert"
  name?: string
  agent_config: FlightStatusAlertConfig
}

export interface UpdateProactiveAgentPayload {
  name?: string
  status?: ProactiveAgentStatus
  agent_config?: HotelPriceAlertConfig
  customize?: boolean
  /** Claims an auto-created alert for the customer. One-way, so nothing else is accepted. */
  created_by?: "user_created"
}

export interface CurrentHotelPriceResponse {
  price: number | null
  currency: string
  entity_ref: string | null
}

export type RunOutcome = "matched" | "checked" | "deferred" | "error"

export interface ProactiveAgentRunDetails extends Record<string, unknown> {
  current_price?: number
  currency?: string
  target_price?: number
  reference_price?: number
  config_revision?: string
  watch_key?: string
  journey_label?: string
  flight_label?: string
  journeys?: FlightPriceAlertJourney[]
  slices?: FlightPriceAlertSlice[]
  selected_options?: string[]
  hotel_room_entity_ref?: string
  provider_room_id?: string
  room_name?: string
}

export interface AgentRun {
  id: number
  ref: AgentRunRef
  outcome: RunOutcome
  summary?: string
  alerted: boolean
  details: ProactiveAgentRunDetails
  duration_ms?: number
  origin?: string
  created_at: string
  /** at or under target and still describing the watch's current settings */
  deal: boolean
  /** false when the check ran under settings the watch has since changed */
  matches_current_config: boolean
}

export interface ProactiveAgentRunsResponse {
  runs: AgentRun[]
}

/**
 * A completed run's summary in the home feed; the ref fetches the full
 * report. `status` tells it apart from a run still working in the same list.
 */
export interface AgentRunSummary {
  status: "complete"
  ref: AgentRunRef
  /** the agent that produced this report; absent on runs from before it shipped */
  agent_ref?: AgentRef
  agent_name: string
  headline: string | null
  /** feed blurb: 2-4 sentences of markdown, place names in bold; absent on older runs */
  summary_md?: string
  /**
   * The feed card's magazine-cover display title, two to four words; null on
   * runs from before covers existed, where the card sets the headline instead.
   */
  cover_title?: string | null
  /** the names worth opening the report for */
  cover_line?: string | null
  /** small print: counts of what the report recommends */
  cover_tally?: string | null
  /** mentioned places as opaque refs — card leads first, then other mentions, up to five */
  entity_refs: string[]
  created_at: Timestamp
  /** card image — the first mentioned place's lead photo */
  photo?: PlacePhoto
}
